import React, { useState, useEffect } from 'react';
import { 
  Activity, CheckCircle, XCircle, Clock, Zap, 
  MessageSquare, Send, Database, TrendingUp, AlertTriangle,
  Bot, Workflow, Globe, Server, BarChart3
} from 'lucide-react';
import KPIDashboard from './KPIDashboard';

const AgentMonitoringDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [agents, setAgents] = useState([
    {
      id: 'gemini-ai',
      name: 'Gemini AI Agent',
      type: 'AI Model',
      status: 'active',
      icon: '🤖',
      color: '#667eea',
      requests: 156,
      successRate: 98.5,
      avgResponseTime: '1.2s',
      lastActive: 'منذ 5 ثوانٍ',
      endpoints: [
        { name: 'Chat Completion', calls: 89, success: 88 },
        { name: 'Text Analysis', calls: 45, success: 45 },
        { name: 'Classification', calls: 22, success: 21 }
      ]
    },
    {
      id: 'n8n-workflow',
      name: 'n8n Automation',
      type: 'Workflow Engine',
      status: 'active',
      icon: '⚡',
      color: '#f59e0b',
      requests: 234,
      successRate: 99.2,
      avgResponseTime: '0.8s',
      lastActive: 'منذ 2 ثانية',
      endpoints: [
        { name: 'Webhook Handler', calls: 120, success: 120 },
        { name: 'Email Sender', calls: 67, success: 66 },
        { name: 'SMS Gateway', calls: 47, success: 47 }
      ]
    },
    {
      id: 'database-agent',
      name: 'Database Manager',
      type: 'Data Layer',
      status: 'active',
      icon: '💾',
      color: '#10b981',
      requests: 892,
      successRate: 100,
      avgResponseTime: '0.3s',
      lastActive: 'منذ 1 ثانية',
      endpoints: [
        { name: 'Read Operations', calls: 567, success: 567 },
        { name: 'Write Operations', calls: 234, success: 234 },
        { name: 'Cache Queries', calls: 91, success: 91 }
      ]
    },
    {
      id: 'notification-agent',
      name: 'Notification Service',
      type: 'Communication',
      status: 'active',
      icon: '🔔',
      color: '#ec4899',
      requests: 445,
      successRate: 97.8,
      avgResponseTime: '0.5s',
      lastActive: 'منذ 3 ثوانٍ',
      endpoints: [
        { name: 'Push Notifications', calls: 234, success: 230 },
        { name: 'Email Queue', calls: 156, success: 154 },
        { name: 'SMS Queue', calls: 55, success: 54 }
      ]
    }
  ]);

  const [apiCalls, setApiCalls] = useState([
    { time: '06:40:15', agent: 'Gemini AI', endpoint: 'Chat Completion', status: 'success', duration: '1.1s', user: 'فاطمة العنزي' },
    { time: '06:40:12', agent: 'n8n Workflow', endpoint: 'Webhook Handler', status: 'success', duration: '0.7s', user: 'System' },
    { time: '06:40:08', agent: 'Database', endpoint: 'Read Operations', status: 'success', duration: '0.2s', user: 'أحمد المالكي' },
    { time: '06:40:05', agent: 'Notification', endpoint: 'Push Notifications', status: 'success', duration: '0.4s', user: 'سارة الأحمدي' },
    { time: '06:40:02', agent: 'Gemini AI', endpoint: 'Text Analysis', status: 'success', duration: '1.3s', user: 'فاطمة العنزي' },
    { time: '06:39:58', agent: 'n8n Workflow', endpoint: 'Email Sender', status: 'failed', duration: '2.1s', user: 'System' },
    { time: '06:39:55', agent: 'Database', endpoint: 'Write Operations', status: 'success', duration: '0.3s', user: 'أحمد المالكي' },
    { time: '06:39:50', agent: 'Gemini AI', endpoint: 'Classification', status: 'success', duration: '0.9s', user: 'سارة الأحمدي' }
  ]);

  const [systemStats, setSystemStats] = useState({
    totalRequests: 1727,
    successfulRequests: 1698,
    failedRequests: 29,
    avgResponseTime: '0.7s',
    uptime: '99.8%',
    activeAgents: 4,
    totalAgents: 4
  });

  // محاكاة تحديث البيانات في الوقت الفعلي
  useEffect(() => {
    const interval = setInterval(() => {
      // تحديث آخر نشاط
      setAgents(prev => prev.map(agent => ({
        ...agent,
        requests: agent.requests + Math.floor(Math.random() * 3),
        lastActive: 'منذ ' + Math.floor(Math.random() * 10) + ' ثوانٍ'
      })));

      // إضافة API call جديد
      const newCall = {
        time: new Date().toLocaleTimeString('ar-SA'),
        agent: ['Gemini AI', 'n8n Workflow', 'Database', 'Notification'][Math.floor(Math.random() * 4)],
        endpoint: 'API Call',
        status: Math.random() > 0.1 ? 'success' : 'failed',
        duration: (Math.random() * 2).toFixed(1) + 's',
        user: ['فاطمة العنزي', 'أحمد المالكي', 'سارة الأحمدي', 'System'][Math.floor(Math.random() * 4)]
      };
      
      setApiCalls(prev => [newCall, ...prev.slice(0, 19)]);
      
      // تحديث الإحصائيات
      setSystemStats(prev => ({
        ...prev,
        totalRequests: prev.totalRequests + 1,
        successfulRequests: prev.successfulRequests + (newCall.status === 'success' ? 1 : 0),
        failedRequests: prev.failedRequests + (newCall.status === 'failed' ? 1 : 0)
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const OverviewTab = () => (
    <div className="space-y-6">
      {/* إحصائيات النظام */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{systemStats.totalRequests}</span>
          </div>
          <p className="text-sm opacity-90">إجمالي الطلبات</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{systemStats.successfulRequests}</span>
          </div>
          <p className="text-sm opacity-90">طلبات ناجحة</p>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <XCircle className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{systemStats.failedRequests}</span>
          </div>
          <p className="text-sm opacity-90">طلبات فاشلة</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{systemStats.avgResponseTime}</span>
          </div>
          <p className="text-sm opacity-90">متوسط وقت الاستجابة</p>
        </div>
      </div>

      {/* حالة الـ Agents */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Bot className="w-6 h-6 text-indigo-600" />
          حالة الوكلاء الذكية
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {agents.map(agent => (
            <div 
              key={agent.id}
              className="border-2 rounded-xl p-4 hover:shadow-lg transition-all"
              style={{ borderColor: agent.color }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                    style={{ background: agent.color + '20' }}
                  >
                    {agent.icon}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800">{agent.name}</h4>
                    <p className="text-sm text-gray-500">{agent.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-green-600">نشط</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-4">
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{ color: agent.color }}>{agent.requests}</p>
                  <p className="text-xs text-gray-500">طلبات</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{ color: agent.color }}>{agent.successRate}%</p>
                  <p className="text-xs text-gray-500">نجاح</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{ color: agent.color }}>{agent.avgResponseTime}</p>
                  <p className="text-xs text-gray-500">استجابة</p>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t">
                <p className="text-xs text-gray-500">آخر نشاط: {agent.lastActive}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const ApiCallsTab = () => (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
        <Send className="w-6 h-6 text-indigo-600" />
        سجل الإرسالات والطلبات (Real-time)
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-right p-3 text-sm font-bold text-gray-700">الوقت</th>
              <th className="text-right p-3 text-sm font-bold text-gray-700">الوكيل</th>
              <th className="text-right p-3 text-sm font-bold text-gray-700">Endpoint</th>
              <th className="text-right p-3 text-sm font-bold text-gray-700">المستخدم</th>
              <th className="text-right p-3 text-sm font-bold text-gray-700">الحالة</th>
              <th className="text-right p-3 text-sm font-bold text-gray-700">المدة</th>
            </tr>
          </thead>
          <tbody>
            {apiCalls.map((call, idx) => (
              <tr 
                key={idx} 
                className={`border-b hover:bg-gray-50 transition-colors ${idx === 0 ? 'bg-blue-50' : ''}`}
              >
                <td className="p-3 text-sm text-gray-600">{call.time}</td>
                <td className="p-3">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-sm font-medium">
                    {call.agent}
                  </span>
                </td>
                <td className="p-3 text-sm text-gray-700">{call.endpoint}</td>
                <td className="p-3 text-sm text-gray-600">{call.user}</td>
                <td className="p-3">
                  {call.status === 'success' ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      نجح
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm font-medium">
                      <XCircle className="w-4 h-4" />
                      فشل
                    </span>
                  )}
                </td>
                <td className="p-3 text-sm text-gray-600">{call.duration}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const EndpointsTab = () => (
    <div className="space-y-6">
      {agents.map(agent => (
        <div key={agent.id} className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
              style={{ background: agent.color + '20' }}
            >
              {agent.icon}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">{agent.name}</h3>
              <p className="text-sm text-gray-500">{agent.type}</p>
            </div>
          </div>

          <div className="space-y-3">
            {agent.endpoints.map((endpoint, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="font-medium text-gray-800">{endpoint.name}</p>
                    <p className="text-sm text-gray-500">
                      {endpoint.calls} طلبات • {endpoint.success} ناجح
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <p className="text-sm font-bold text-green-600">
                      {((endpoint.success / endpoint.calls) * 100).toFixed(1)}%
                    </p>
                    <p className="text-xs text-gray-500">نسبة النجاح</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold mb-2 flex items-center gap-3">
              <Server className="w-8 h-8" />
              مراقبة الوكلاء الذكية
            </h2>
            <p className="text-indigo-100">
              مراقبة شاملة لجميع الـ Agents، n8n Workflows، والإرسالات
            </p>
          </div>
          <div className="text-center">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">النظام يعمل</span>
            </div>
            <p className="text-2xl font-bold">{systemStats.uptime}</p>
            <p className="text-xs text-indigo-200">وقت التشغيل</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
              activeTab === 'overview'
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Activity className="w-5 h-5" />
            نظرة عامة
          </button>
          <button
            onClick={() => setActiveTab('api-calls')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
              activeTab === 'api-calls'
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Send className="w-5 h-5" />
            سجل الإرسالات
          </button>
          <button
            onClick={() => setActiveTab('endpoints')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
              activeTab === 'endpoints'
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Globe className="w-5 h-5" />
            Endpoints
          </button>
          <button
            onClick={() => setActiveTab('kpis')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
              activeTab === 'kpis'
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <BarChart3 className="w-5 h-5" />
            مؤشرات الأداء
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'api-calls' && <ApiCallsTab />}
      {activeTab === 'endpoints' && <EndpointsTab />}
      {activeTab === 'kpis' && <KPIDashboard />}
    </div>
  );
};

export default AgentMonitoringDashboard;
